import React, { useState, useEffect } from 'react';
import { Plus, Eye, Edit2, Trash2, FileText, Download, Calendar, ChevronDown, ChevronUp, Package, Upload, X } from 'lucide-react';
import DataTable from '../../../components/common/DataTable';
import SPKForm from './SPKForm';
import toast from 'react-hot-toast';
import { 
  getAllSPKsPaged, 
  getSPKById, 
  softDeleteSPK,
  getSPKsByProjectType,
  searchSPKs,
  SPKStatus,
  getStatusDisplayName,
  getStatusBadgeClass,
  ProjectType,
  triggerDocumentDownload,
  uploadDocument
} from '../../../services/spkService';

const SPKList = () => {
  const [view, setView] = useState('list');
  const [selectedSPK, setSelectedSPK] = useState(null);
  const [spkList, setSPKList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalElements, setTotalElements] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortConfig, setSortConfig] = useState({ sortBy: 'spkDate', direction: 'DESC' });
  const [searchValue, setSearchValue] = useState('');
  const [filterType, setFilterType] = useState('ALL');
  const [stats, setStats] = useState({ total: 0, tower: 0, fo: 0 });

  useEffect(() => { fetchSPKs(); }, [currentPage, pageSize, sortConfig, filterType]);
  useEffect(() => { fetchStatistics(); }, []);

  const fetchSPKs = async () => {
    setIsLoading(true);
    try {
      const sortParam = `${sortConfig.sortBy},${sortConfig.direction.toLowerCase()}`;
      let response;
      if (searchValue.trim()) {
        response = await searchSPKs(searchValue, currentPage, pageSize);
      } else if (filterType !== 'ALL') {
        response = await getSPKsByProjectType(filterType, currentPage, pageSize);
      } else {
        response = await getAllSPKsPaged(currentPage, pageSize, sortParam);
      }
      setSPKList(response.content);
      setTotalElements(response.totalElements);
    } catch (error) {
      console.error('Error fetching SPKs:', error);
      toast.error('Gagal memuat data SPK');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const allResponse = await getAllSPKsPaged(0, 1);
      const towerResponse = await getSPKsByProjectType(ProjectType.TOWER, 0, 1);
      const foResponse = await getSPKsByProjectType(ProjectType.FO, 0, 1);
      setStats({
        total: allResponse.totalElements,
        tower: towerResponse.totalElements,
        fo: foResponse.totalElements
      });
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const columns = [
    {
      key: 'no', label: 'No', sortable: false, width: 'w-16',
      render: (row, index) => <span className="font-medium text-gray-900">{currentPage * pageSize + index + 1}</span>
    },
    {
      key: 'customerName', label: 'Customer', sortable: true, nowrap: false,
      render: (row) => (
        <div>
          <div className="font-semibold text-gray-900">{row.customerName}</div>
          <div className="text-xs text-gray-500 mt-1">{row.customerCode}</div>
        </div>
      )
    },
    {
      key: 'spkNumber', label: 'SPK Number', sortable: true,
      render: (row) => <span className="font-bold text-blue-600">{row.spkNumber}</span>
    },
    {
      key: 'projectName', label: 'Project Name', sortable: true, nowrap: false,
      render: (row) => <span className="text-gray-900 font-medium">{row.projectName}</span>
    },
    {
      key: 'spkDate', label: 'SPK Date', sortable: true,
      render: (row) => (
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-gray-400" />
          <span className="text-gray-700">{formatDate(row.spkDate)}</span>
        </div>
      )
    },
    {
      key: 'projectType', label: 'Type', sortable: true,
      render: (row) => (
        <span className={`px-3 py-1 text-xs font-semibold rounded-full ${row.projectType === 'TOWER' ? 'bg-purple-100 text-purple-800' : 'bg-green-100 text-green-800'}`}>
          {row.projectType}
        </span>
      )
    },
    {
      key: 'status', label: 'Status', sortable: true,
      render: (row) => (
        <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getStatusBadgeClass(row.status)}`}>
          {getStatusDisplayName(row.status)}
        </span>
      )
    },
    {
      key: 'items', label: 'Items', sortable: false,
      render: (row) => (
        <div className="flex items-center gap-2">
          <Package className="w-4 h-4 text-blue-600" />
          <span className="font-semibold text-blue-600">
            {row.totalItems || 0} {row.projectType === 'TOWER' ? 'Sites' : 'Segments'}
          </span>
        </div>
      )
    },
    {
      key: 'deliveryPeriod', label: 'Delivery', sortable: true,
      render: (row) => <span className="font-medium text-gray-700">{row.deliveryPeriod} Months</span>
    },
    {
      key: 'actions', label: 'Actions', sortable: false,
      render: (row) => (
        <div className="flex gap-2">
          <button onClick={() => handleView(row)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="View Details">
            <Eye className="w-5 h-5" />
          </button>
          <button onClick={() => handleEdit(row)} className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors" title="Edit">
            <Edit2 className="w-5 h-5" />
          </button>
          <button onClick={() => handleDelete(row)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Delete" disabled={row.status === SPKStatus.CONVERTED}>
            <Trash2 className="w-5 h-5" />
          </button>
        </div>
      )
    }
  ];

  const handleAddNew = () => { setView('add'); setSelectedSPK(null); };

  const handleView = async (spk) => {
    try {
      setIsLoading(true);
      const fullSPK = await getSPKById(spk.id);
      setSelectedSPK(fullSPK);
      setView('detail');
    } catch (error) {
      toast.error('Gagal memuat detail SPK');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = async (spk) => {
    try {
      setIsLoading(true);
      const fullSPK = await getSPKById(spk.id);
      setSelectedSPK(fullSPK);
      setView('edit');
    } catch (error) {
      toast.error('Gagal memuat detail SPK');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (spk) => {
    if (spk.status === SPKStatus.CONVERTED) {
      toast.error('Tidak dapat menghapus SPK yang sudah dikonversi ke Project!');
      return;
    }
    if (window.confirm(`Apakah Anda yakin ingin menghapus SPK ${spk.spkNumber}?`)) {
      try {
        await softDeleteSPK(spk.id);
        toast.success('SPK berhasil dihapus!');
        fetchSPKs();
        fetchStatistics();
      } catch (error) {
        toast.error('Gagal menghapus SPK');
      }
    }
  };

  const handleBackToList = () => {
    setView('list');
    setSelectedSPK(null);
    fetchSPKs();
    fetchStatistics();
  };

  if (view === 'add' || view === 'edit') {
    return <SPKForm onBack={handleBackToList} spkData={selectedSPK} mode={view} />;
  }

  if (view === 'detail' && selectedSPK) {
    return <SPKDetail spk={selectedSPK} onBack={handleBackToList} onEdit={() => handleEdit(selectedSPK)} />;
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-blue-900 to-blue-700 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-200 text-sm font-medium">Total SPKs</p>
              <p className="text-3xl font-bold mt-2">{stats.total}</p>
              <p className="text-blue-200 text-sm mt-2">Active work orders</p>
            </div>
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
              <FileText className="w-6 h-6" />
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-purple-600 to-purple-500 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm font-medium">Tower Projects</p>
              <p className="text-3xl font-bold mt-2">{stats.tower}</p>
              <p className="text-purple-100 text-sm mt-2">Tower installations</p>
            </div>
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
              <FileText className="w-6 h-6" />
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-green-600 to-green-500 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium">FO Projects</p>
              <p className="text-3xl font-bold mt-2">{stats.fo}</p>
              <p className="text-green-100 text-sm mt-2">Fiber optic projects</p>
            </div>
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
              <FileText className="w-6 h-6" />
            </div>
          </div>
        </div>
      </div>

      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg flex items-center justify-center shadow-md">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-800">SPK List</h2>
              <p className="text-gray-600 mt-1">Manage Surat Perintah Kerja (Work Orders)</p>
            </div>
          </div>
          <button onClick={handleAddNew} className="bg-red-600 hover:bg-red-700 text-white font-medium px-6 py-3 rounded-lg flex items-center gap-2 transition-colors shadow-md hover:shadow-lg">
            <Plus className="w-5 h-5" />
            Add SPK
          </button>
        </div>
      </div>

      {/* Filter by Type */}
      <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
        <div className="flex items-center gap-4">
          <span className="text-sm font-semibold text-gray-700">Filter by Type:</span>
          <div className="flex gap-2">
            <button onClick={() => { setFilterType('ALL'); setCurrentPage(0); }} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filterType === 'ALL' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
              All Types ({stats.total})
            </button>
            <button onClick={() => { setFilterType(ProjectType.TOWER); setCurrentPage(0); }} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filterType === ProjectType.TOWER ? 'bg-purple-600 text-white' : 'bg-purple-100 text-purple-700 hover:bg-purple-200'}`}>
              TOWER ({stats.tower})
            </button>
            <button onClick={() => { setFilterType(ProjectType.FO); setCurrentPage(0); }} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filterType === ProjectType.FO ? 'bg-green-600 text-white' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}>
              FO ({stats.fo})
            </button>
          </div>
        </div>
      </div>

      {/* DataTable */}
      <DataTable
        columns={columns}
        data={spkList}
        currentPage={currentPage}
        pageSize={pageSize}
        totalElements={totalElements}
        onPageChange={setCurrentPage}
        onPageSizeChange={(s) => { setPageSize(s); setCurrentPage(0); }}
        onSort={(sc) => { setSortConfig(sc); setCurrentPage(0); }}
        sortConfig={sortConfig}
        isLoading={isLoading}
        onSearch={(v) => { setSearchValue(v); setCurrentPage(0); setTimeout(fetchSPKs, 500); }}
        searchValue={searchValue}
        searchPlaceholder="Search by SPK number, customer, or project name..."
        enableQuickJump={true}
        customPageSizes={[5, 10, 25, 50]}
        striped={false}
        headerBgColor="bg-gradient-to-r from-red-600 to-red-500"
        headerTextColor="text-white"
        hoverColor="hover:bg-red-50"
      />
    </div>
  );
};

// ============================================================================
// SPK DETAIL COMPONENT
// ============================================================================
const SPKDetail = ({ spk, onBack, onEdit }) => {
  const [expandedPanel, setExpandedPanel] = useState('basic');
  // Local documents state — seeded from spk.documents, bisa bertambah via upload
  const [localDocuments, setLocalDocuments] = useState(spk.documents || []);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const handleDownloadDocument = async (doc) => {
    try {
      await triggerDocumentDownload(spk.id, doc.id, doc.fileName);
      toast.success('Document downloaded successfully!');
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  // Upload dokumen baru dari halaman detail
  const handleUploadDocument = async (docData) => {
    setIsUploading(true);
    try {
      const uploaded = await uploadDocument(spk.id, docData.file, docData.description);
      setLocalDocuments(prev => [...prev, uploaded]);
      setShowUploadModal(false);
      toast.success('Dokumen berhasil diupload!');
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error('Gagal mengupload dokumen');
    } finally {
      setIsUploading(false);
    }
  };

  const Panel = ({ title, name, children }) => (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setExpandedPanel(expandedPanel === name ? null : name)}
        className={`w-full px-6 py-4 flex items-center justify-between transition-colors ${expandedPanel === name ? 'bg-gradient-to-r from-blue-50 to-red-50' : 'bg-gray-50 hover:bg-gray-100'}`}
      >
        <h3 className={`text-lg font-bold ${expandedPanel === name ? 'text-red-600' : 'text-gray-800'}`}>{title}</h3>
        {expandedPanel === name ? <ChevronUp className="w-5 h-5 text-red-600" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
      </button>
      {expandedPanel === name && <div className="p-6 bg-white">{children}</div>}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg flex items-center justify-center shadow-md">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-800">SPK Details</h2>
              <p className="text-gray-600 mt-1">{spk.spkNumber}</p>
            </div>
          </div>
          <button onClick={onEdit} className="bg-red-600 hover:bg-red-700 text-white font-medium px-6 py-3 rounded-lg flex items-center gap-2 transition-colors shadow-md hover:shadow-lg">
            <Edit2 className="w-5 h-5" />
            Edit SPK
          </button>
        </div>
      </div>

      {/* Accordion Panels */}
      <div className="space-y-4">

        {/* SPK Information Panel */}
        <Panel title="SPK Information" name="basic">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <label className="block text-sm font-semibold text-blue-900 mb-2">Customer</label>
              <p className="text-lg text-gray-900 font-semibold">{spk.customerName}</p>
              <p className="text-sm text-gray-600 mt-1">Customer Code: {spk.customerCode}</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <label className="block text-sm font-medium text-gray-500 mb-2">SPK Number</label>
              <p className="text-lg text-gray-900 font-bold">{spk.spkNumber}</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <label className="block text-sm font-medium text-gray-500 mb-2">SPK Date</label>
              <p className="text-lg text-gray-900">{formatDate(spk.spkDate)}</p>
            </div>
            <div className="md:col-span-2 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <label className="block text-sm font-medium text-gray-500 mb-2">Project Name</label>
              <p className="text-lg text-gray-900 font-semibold">{spk.projectName}</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <label className="block text-sm font-medium text-gray-500 mb-2">Project Type</label>
              <span className={`px-4 py-2 inline-flex text-sm font-semibold rounded-full ${spk.projectType === 'TOWER' ? 'bg-purple-100 text-purple-800' : 'bg-green-100 text-green-800'}`}>
                {spk.projectType}
              </span>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <label className="block text-sm font-medium text-gray-500 mb-2">Delivery Period</label>
              <p className="text-lg text-gray-900 font-semibold">{spk.deliveryPeriod} Months</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <label className="block text-sm font-medium text-gray-500 mb-2">Status</label>
              <span className={`px-4 py-2 inline-flex text-sm font-semibold rounded-full ${getStatusBadgeClass(spk.status)}`}>
                {getStatusDisplayName(spk.status)}
              </span>
            </div>
            {spk.notes && (
              <div className="md:col-span-2 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <label className="block text-sm font-medium text-gray-500 mb-2">Notes</label>
                <p className="text-base text-gray-900">{spk.notes}</p>
              </div>
            )}

            {/* ============================================================ */}
            {/* SPK DOCUMENTS SECTION                                        */}
            {/* ============================================================ */}
            <div className="md:col-span-2">
              {/* Header baris — judul + tombol Add Document */}
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-semibold text-gray-700">
                  SPK Documents ({localDocuments.length})
                </label>
                <button
                  onClick={() => setShowUploadModal(true)}
                  disabled={isUploading}
                  className="bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium px-4 py-2 rounded-lg flex items-center gap-2 transition-colors shadow-md hover:shadow-lg text-sm"
                >
                  <Upload className="w-4 h-4" />
                  {isUploading ? 'Uploading...' : 'Add Document'}
                </button>
              </div>

              {localDocuments.length > 0 ? (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gradient-to-r from-blue-600 to-blue-500">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider w-12">No</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">Document Name</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">Description</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider w-32">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {localDocuments.map((doc, index) => (
                        <tr key={doc.id} className="hover:bg-blue-50 transition-colors">
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{index + 1}</td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                                <FileText className="w-5 h-5 text-white" />
                              </div>
                              <div>
                                <p className="text-sm font-semibold text-gray-900">{doc.fileName}</p>
                                <p className="text-xs text-gray-500">{doc.fileType}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3"><p className="text-sm text-gray-700">{doc.description || '-'}</p></td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <button onClick={() => handleDownloadDocument(doc)} className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 font-medium px-3 py-1.5 rounded-lg flex items-center gap-1 transition-colors text-xs">
                              <Download className="w-3 h-3" />
                              Download
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                /* Empty state dengan tombol Upload yang prominent */
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center bg-gray-50">
                  <div className="w-14 h-14 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <FileText className="w-7 h-7 text-gray-400" />
                  </div>
                  <p className="text-gray-600 font-medium mb-1">Belum ada dokumen SPK</p>
                  <p className="text-sm text-gray-500 mb-5">Upload dokumen SPK atau dokumen pendukung lainnya</p>
                  <button
                    onClick={() => setShowUploadModal(true)}
                    disabled={isUploading}
                    className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium px-6 py-2.5 rounded-lg transition-colors shadow-md hover:shadow-lg"
                  >
                    <Upload className="w-4 h-4" />
                    {isUploading ? 'Uploading...' : 'Upload Document'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </Panel>

        {/* Sites/Segments Panel */}
        <Panel
          title={spk.projectType === 'TOWER' ? `Tower Sites (${spk.towerSites?.length || 0})` : `FO Segments (${spk.foSegments?.length || 0})`}
          name="items"
        >
          {spk.projectType === 'TOWER' ? (
            <div className="space-y-3">
              {spk.towerSites && spk.towerSites.length > 0 ? (
                spk.towerSites.map((site, index) => (
                  <div key={site.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-purple-700 rounded-lg flex items-center justify-center text-white font-bold shadow-md">{index + 1}</div>
                        <div>
                          <div className="font-semibold text-gray-900">{site.siteName}</div>
                          <div className="text-sm text-gray-600 mt-1">{site.siteId} • {site.location} • {site.regional}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-gray-900 text-lg">{site.towerHeight} m</div>
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800 mt-1">{site.typeSiteContract}</span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">No tower sites available</div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {spk.foSegments && spk.foSegments.length > 0 ? (
                spk.foSegments.map((segment, index) => (
                  <div key={segment.id} className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                    <div className="bg-gradient-to-r from-green-50 to-blue-50 px-4 py-3 border-b border-gray-200">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-green-600 to-green-700 rounded-lg flex items-center justify-center text-white font-bold shadow-md">{index + 1}</div>
                        <div className="flex-1">
                          <div className="font-bold text-gray-900 text-lg">{segment.segmentName}</div>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-green-600 text-white">{segment.ringType}</span>
                            <span className="text-xs text-gray-600">Span ID: {segment.spanId}</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-green-600">{(segment.length / 1000).toFixed(2)} km</div>
                          <div className="text-xs text-gray-500 mt-1">{segment.length?.toLocaleString() || 0} m</div>
                        </div>
                      </div>
                    </div>
                    <div className="p-4 bg-white">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Area / Province</label>
                            <p className="text-sm font-semibold text-gray-900">{segment.area}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Kota / Kabupaten</label>
                            <p className="text-sm font-semibold text-gray-900">{segment.kota}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">No segments available</div>
              )}
            </div>
          )}
        </Panel>
      </div>

      {/* Upload Document Modal */}
      {showUploadModal && (
        <UploadDocumentModal
          onUpload={handleUploadDocument}
          onClose={() => setShowUploadModal(false)}
          isUploading={isUploading}
        />
      )}
    </div>
  );
};

// ============================================================================
// UPLOAD DOCUMENT MODAL
// Digunakan di SPKDetail untuk upload dokumen baru.
// Interface identik dengan AddDocumentModal di SPKForm.
// ============================================================================
const UploadDocumentModal = ({ onUpload, onClose, isUploading }) => {
  const [formData, setFormData] = useState({ file: null, fileName: '', fileType: '', fileUrl: null, description: '' });
  const [fileError, setFileError] = useState('');
  const [errors, setErrors] = useState({});

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) { setFileError('Only PDF, JPG, and PNG files are allowed'); return; }
    if (file.size > 5 * 1024 * 1024) { setFileError('File size must be less than 5MB'); return; }
    const fileTypeLabel = file.type.includes('image') ? file.type.split('/')[1].toUpperCase() : 'PDF';
    setFormData(prev => ({ ...prev, file, fileName: file.name, fileType: fileTypeLabel, fileUrl: URL.createObjectURL(file) }));
    setFileError('');
    setErrors(prev => ({ ...prev, file: '' }));
  };

  const handleRemoveFile = () => {
    setFormData(prev => ({ ...prev, file: null, fileName: '', fileType: '', fileUrl: null }));
    const input = document.getElementById('detailDocFileInput');
    if (input) input.value = '';
  };

  const validate = () => {
    const e = {};
    if (!formData.file) e.file = 'Document file is required';
    if (!formData.description.trim()) e.description = 'Description is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e) => { e.preventDefault(); if (validate()) onUpload(formData); };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-red-50">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-xl font-bold text-gray-800">Upload Document</h3>
              <p className="text-sm text-gray-600 mt-1">Upload SPK or supporting document</p>
            </div>
            <button onClick={onClose} disabled={isUploading} className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {/* File Upload */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Document File <span className="text-red-600">*</span>
            </label>
            {!formData.fileUrl ? (
              <div className={`border-2 border-dashed rounded-lg p-6 transition-all ${errors.file || fileError ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'}`}>
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto bg-blue-100 rounded-full flex items-center justify-center mb-4">
                    <Upload className="w-8 h-8 text-blue-600" />
                  </div>
                  <label htmlFor="detailDocFileInput" className="cursor-pointer bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-3 rounded-lg inline-flex items-center gap-2 transition-all shadow-md hover:shadow-lg">
                    <Upload className="w-4 h-4" />
                    Select File
                  </label>
                  <input id="detailDocFileInput" type="file" onChange={handleFileSelect} accept=".pdf,.jpg,.jpeg,.png" className="hidden" />
                  <p className="text-sm text-gray-600 mt-3">Click to upload document</p>
                  <p className="text-xs text-gray-500 mt-1">PDF, JPG, or PNG (Max 5MB)</p>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between bg-gradient-to-r from-blue-50 to-green-50 p-4 rounded-lg border border-blue-200">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                    <FileText className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{formData.fileName}</p>
                    <p className="text-xs text-green-600 mt-1">✓ File selected ({formData.fileType})</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <a href={formData.fileUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 text-sm font-medium hover:underline">Preview</a>
                  <button type="button" onClick={handleRemoveFile} className="p-2 hover:bg-red-100 rounded-lg transition-colors">
                    <X className="w-5 h-5 text-red-500" />
                  </button>
                </div>
              </div>
            )}
            {(errors.file || fileError) && <p className="mt-2 text-sm text-red-600">⚠ {errors.file || fileError}</p>}
          </div>

          {/* Description */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Document Description <span className="text-red-600">*</span>
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={(e) => { setFormData(prev => ({ ...prev, description: e.target.value })); setErrors(prev => ({ ...prev, description: '' })); }}
              rows="3"
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none ${errors.description ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
              placeholder="e.g., SPK Document - Original, Addendum 1, Technical Drawing, etc."
            />
            {errors.description && <p className="mt-1 text-sm text-red-600">⚠ {errors.description}</p>}
            <p className="mt-1 text-xs text-gray-500">Provide a brief description of this document</p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button type="button" onClick={onClose} disabled={isUploading} className="border border-gray-300 text-gray-700 hover:bg-gray-100 font-medium px-6 py-2 rounded-lg transition-colors disabled:opacity-50">
              Cancel
            </button>
            <button type="submit" disabled={isUploading} className="bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium px-6 py-2 rounded-lg transition-all shadow-md hover:shadow-lg flex items-center gap-2">
              <Upload className="w-4 h-4" />
              {isUploading ? 'Uploading...' : 'Upload Document'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SPKList;